import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import { listBillingAccounts } from "../../api/payments";
import {
  getIssuedInvoicePdf,
  getIssuedReceiptPdf,
  listIssuedInvoices,
  listIssuedReceipts,
} from "../../api/issuedBilling";
import type { AnkhInvoice, AnkhReceipt } from "../../api/ankhInvoices";
import { istDate, pdfFileName, saveBlob } from "./AnkhInvoices";
import { DeliveryChip } from "./DeliveryChip";

const inr = (n: number) => n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function issuedErrorTitle(e: Error, what: string): string {
  if (e instanceof ApiError) {
    if (e.status === 409) return "This invoice is not issued yet, so it has no PDF";
    switch (e.kind) {
      case "fail-closed":
        return `Nothing was sent for ${what}`;
      case "unauthenticated":
        return "Your sign-in has expired -- sign in again";
      case "forbidden":
        return "Refused -- your role cannot read this namespace's invoices";
      case "not-found":
        return `Not found: ${what}`;
      case "at-capacity":
        return "The service is busy -- try again shortly";
      case "bad-request":
        return `The request for ${what} was refused`;
      case "unavailable":
        return `Could not reach the service for ${what}`;
    }
  }
  return `Could not complete ${what}`;
}

function ErrorAlert({ error, what }: { error: Error; what: string }) {
  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      <AlertTitle>{issuedErrorTitle(error, what)}</AlertTitle>
      {error.message}
    </Alert>
  );
}

/**
 * Billing › Issued invoices: what THIS namespace billed one of its customers,
 * and the receipts for that customer's payments. The seller's ledger -- the
 * counterpart of "Invoices from Ankh", which is the buyer's.
 */
export function IssuedBillingScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const [accountId, setAccountId] = useState("");
  const [tab, setTab] = useState<"invoices" | "receipts">("invoices");

  const accounts = useQuery({
    queryKey: ["billing-accounts", ns],
    queryFn: ({ signal }) => listBillingAccounts(identity, ns, signal),
    enabled: Boolean(identity.token && ns),
    retry: false,
  });

  if (!ns) {
    return <Alert severity="warning">No namespace could be resolved for your sign-in, so there is nothing to list.</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Issued invoices
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Invoices <code>{ns}</code> issued to a customer, and the receipts for that customer&apos;s payments.
      </Typography>

      {accounts.isLoading && <Typography variant="body2">Loading customers…</Typography>}
      {accounts.isError && <ErrorAlert error={accounts.error as Error} what="the customer list" />}
      {accounts.data && accounts.data.length === 0 && (
        <Alert severity="info">There are no billing accounts in {ns} yet.</Alert>
      )}
      {accounts.data && accounts.data.length > 0 && (
        <TextField
          select
          size="small"
          label="Customer"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          sx={{ minWidth: 360, mb: 2 }}
        >
          {accounts.data.map((a) => (
            <MenuItem key={a.accountid} value={a.accountid}>
              {a.name || a.accountid}
            </MenuItem>
          ))}
        </TextField>
      )}

      {accountId && (
        <>
          <Tabs value={tab} onChange={(_, v: "invoices" | "receipts") => setTab(v)} sx={{ mb: 2 }}>
            <Tab value="invoices" label="Invoices" />
            <Tab value="receipts" label="Receipts" />
          </Tabs>
          {tab === "invoices" ? (
            <InvoiceList ns={ns} accountId={accountId} />
          ) : (
            <ReceiptList ns={ns} accountId={accountId} />
          )}
        </>
      )}
    </Box>
  );
}

function InvoiceList({ ns, accountId }: { ns: string; accountId: string }) {
  const identity = useIdentity();
  const [pdfError, setPdfError] = useState<Error | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["issued-invoices", ns, accountId],
    queryFn: ({ signal }) => listIssuedInvoices(identity, ns, accountId, signal),
    enabled: Boolean(identity.token),
    retry: false,
  });

  async function download(inv: AnkhInvoice) {
    setPdfError(null);
    setBusy(inv.id);
    try {
      saveBlob(await getIssuedInvoicePdf(identity, ns, accountId, inv.id), pdfFileName(inv));
    } catch (e) {
      setPdfError(e as Error);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Box>
      {list.isLoading && <Typography variant="body2">Loading…</Typography>}
      {list.isError && <ErrorAlert error={list.error as Error} what="the invoice list" />}
      {pdfError && <ErrorAlert error={pdfError} what="the PDF" />}
      {list.data && list.data.length === 0 && <Alert severity="info">No invoices issued to this customer yet.</Alert>}
      {list.data && list.data.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="Issued invoices">
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Period</TableCell>
                <TableCell align="right">Amount (₹)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Email</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.data.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell>{inv.invoicenumber || <em>Draft</em>}</TableCell>
                  <TableCell>{istDate(inv.createdat)}</TableCell>
                  <TableCell>
                    {istDate(inv.periodstart)} – {istDate(inv.periodend)}
                  </TableCell>
                  <TableCell align="right">{inr(inv.totalamount)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={inv.syncstatus || "unknown"} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <DeliveryChip delivery={inv.delivery} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" disabled={!inv.invoicenumber || busy === inv.id} onClick={() => void download(inv)}>
                      {busy === inv.id ? "Preparing…" : "PDF"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function ReceiptList({ ns, accountId }: { ns: string; accountId: string }) {
  const identity = useIdentity();
  const [pdfError, setPdfError] = useState<Error | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["issued-receipts", ns, accountId],
    queryFn: ({ signal }) => listIssuedReceipts(identity, ns, accountId, signal),
    enabled: Boolean(identity.token),
    retry: false,
  });

  async function download(rc: AnkhReceipt) {
    setPdfError(null);
    setBusy(rc.orderid);
    try {
      saveBlob(
        await getIssuedReceiptPdf(identity, ns, accountId, rc.orderid),
        pdfFileName({ invoicenumber: rc.receiptnumber, id: rc.orderid }),
      );
    } catch (e) {
      setPdfError(e as Error);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Box>
      {list.isLoading && <Typography variant="body2">Loading…</Typography>}
      {list.isError && <ErrorAlert error={list.error as Error} what="the receipt list" />}
      {pdfError && <ErrorAlert error={pdfError} what="the receipt PDF" />}
      {list.data && list.data.length === 0 && <Alert severity="info">No payment from this customer is recorded yet.</Alert>}
      {list.data && list.data.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="Issued receipts">
            <TableHead>
              <TableRow>
                <TableCell>Receipt</TableCell>
                <TableCell>Received</TableCell>
                <TableCell>For invoice</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell align="right">Amount (₹)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Email</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.data.map((rc) => (
                <TableRow key={rc.id}>
                  <TableCell>{rc.receiptnumber}</TableCell>
                  <TableCell>{istDate(rc.receivedat)}</TableCell>
                  <TableCell>{rc.invoicenumber || "advance"}</TableCell>
                  <TableCell>{rc.paymentmethod}</TableCell>
                  <TableCell>{rc.paymentreference ?? ""}</TableCell>
                  <TableCell align="right">{inr(rc.amount)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={rc.status} color={rc.status === "Issued" ? "success" : "default"} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <DeliveryChip delivery={rc.delivery} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" disabled={busy === rc.orderid} onClick={() => void download(rc)}>
                      {busy === rc.orderid ? "Preparing…" : "PDF"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
